/**
 * Geometry of the top-down "curling house" plot (screens/contour/
 * CurlingHouseView.tsx): the scale, the reference rings actually drawn (with
 * the halving ladder), ring label positions, and each ball's clamped pixel
 * position. Pure: no React Native, no theme import. The phone view and both TV
 * receivers (web/tv/src, web/tv/appletv) draw from this one function so the
 * plot cannot drift between screens. Moved out of CurlingHouseView unchanged.
 */

export type HouseSide = 'challenger' | 'opponent';

export interface HouseBallInput {
  side: HouseSide;
  /** Hit order within that side, 1..5. */
  order: number;
  /** Signed: + short, - past. Plotted vertically (short below the pin). */
  longitudinalMissFt: number;
  /** Signed: + right, - left. Plotted horizontally from the pin. */
  lateralMissFt: number;
}

export interface HouseRingInput {
  radiusFt: number;
  color?: string;
  fillOpacity?: number;
}

export interface CurlingHouseLayoutInput {
  balls: readonly HouseBallInput[];
  width: number;
  height: number;
  lateralExaggeration?: number;
  headroomFactor?: number;
  minimumExtentFt?: number;
  showRingLabels?: boolean;
  ringLabelPosition?: 'right' | 'top';
  edgePaddingPx?: number;
  referenceRings?: readonly HouseRingInput[];
  zoomScale?: number;
  /** Stroke for rings with no color, and for the halving ladder when the
   *  outermost configured ring has none (the phone passes targetRingBlue). */
  defaultRingColor: string;
}

export interface HouseRingDraw {
  radiusFt: number;
  /** Radius in pixels. */
  r: number;
  stroke: string;
  strokeWidth: number;
  fill: string;
  fillOpacity: number;
}

export interface HouseRingLabel {
  radiusFt: number;
  x: number;
  y: number;
  anchor: 'middle' | 'start';
}

export interface CurlingHouseLayout {
  /** Dot and type scale: 1 at a 320 px plot, capped at 2. */
  scale: number;
  ballRadiusPx: number;
  haloRadiusPx: number;
  labelFontPx: number;
  cx: number;
  cy: number;
  ftToPx: number;
  outerRingFt: number;
  maxConfiguredRingFt: number;
  /** Rings to paint, outer to inner (so colored fills stack). */
  rings: HouseRingDraw[];
  /** Label positions for the same rings, in `visibleRings` order. */
  ringLabels: HouseRingLabel[];
  toPxXY: (longFt: number, latFt: number) => { x: number; y: number };
}

export const DEFAULT_HOUSE_RINGS: readonly HouseRingInput[] = [
  { radiusFt: 1 },
  { radiusFt: 3 },
  { radiusFt: 5 },
  { radiusFt: 10 },
];

const BALL_RADIUS_PX = 7;
export const HALO_EXTRA_PX = 5;
const DEFAULT_RING_LABEL_GUTTER_PX = 28;

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/** Dots and type scale with the plot: 320 px is the phone baseline, capped at
 *  2x so a full-screen tablet plot stays tasteful. */
export function housePlotScale(width: number, height: number): number {
  return clamp(Math.min(width, height) / 320, 1, 2);
}

function roundOuterRingFt(targetFt: number): number {
  if (targetFt <= 10) return Math.ceil(targetFt);
  if (targetFt <= 20) return Math.ceil(targetFt / 2) * 2;
  return Math.ceil(targetFt / 5) * 5;
}

export function curlingHouseLayout(input: CurlingHouseLayoutInput): CurlingHouseLayout {
  const {
    balls,
    width,
    height,
    lateralExaggeration = 2.5,
    headroomFactor = 1.2,
    minimumExtentFt = 1,
    showRingLabels = true,
    ringLabelPosition = 'right',
    edgePaddingPx = 28,
    referenceRings = DEFAULT_HOUSE_RINGS,
    zoomScale = 1,
    defaultRingColor,
  } = input;
  const scale = housePlotScale(width, height);
  const ballRadiusPx = BALL_RADIUS_PX * scale;
  const markerInsetPx = ballRadiusPx + HALO_EXTRA_PX * scale + 4;
  const labelFontPx = Math.round(10 * scale);

  const ringLabelGutterPx =
    showRingLabels && ringLabelPosition === 'right' ? DEFAULT_RING_LABEL_GUTTER_PX : 0;
  const cx = width / 2;
  const cy = height / 2;
  const usableR = Math.max(
    24,
    Math.min(width - ringLabelGutterPx, height) / 2 - edgePaddingPx - markerInsetPx,
  );

  // Find the largest miss to set scale.
  let maxR = Math.max(1, Number.isFinite(minimumExtentFt) ? minimumExtentFt : 1);
  for (const b of balls) {
    const r = Math.hypot(b.longitudinalMissFt, b.lateralMissFt * lateralExaggeration);
    if (r > maxR) maxR = r;
  }
  // Pick a reference ring that contains the outer ball with some headroom.
  const target = maxR * headroomFactor;
  const ringRadii = [...referenceRings].map((r) => r.radiusFt).sort((a, b) => a - b);
  const maxConfiguredRingFt = ringRadii[ringRadii.length - 1] ?? 1;
  const outerRingFt =
    ringRadii.find((r) => r >= target) ?? Math.max(roundOuterRingFt(target), maxConfiguredRingFt);

  const ftToPx = (usableR / outerRingFt) * zoomScale;
  const zoomed = zoomScale > 1.001;
  // Overhead, looking down the line of play: lateral miss runs left/right
  // (x), longitudinal runs short (down, toward the player) / past (up).
  const toPxXY = (longFt: number, latFt: number) => {
    const rawX = cx + latFt * lateralExaggeration * ftToPx;
    const rawY = cy + longFt * ftToPx;
    // Fit-all views never hide a marker under the frame; while zoomed,
    // off-window markers leave honestly instead of smearing on the edges.
    if (zoomed) return { x: rawX, y: rawY };
    return {
      x: clamp(rawX, markerInsetPx, width - markerInsetPx),
      y: clamp(rawY, markerInsetPx, height - markerInsetPx),
    };
  };

  // Visible reference rings: only those within the outer ring bound, plus a
  // halving ladder (80/40/20/10...) when one outlier stretches the outer ring
  // far past the configured set.
  const visible: HouseRingInput[] = referenceRings.filter((r) => r.radiusFt <= outerRingFt);
  if (outerRingFt > maxConfiguredRingFt) {
    const outermost = [...referenceRings].sort((a, b) => a.radiusFt - b.radiusFt).pop();
    const ladderColor = outermost?.color ?? defaultRingColor;
    let rung = outerRingFt;
    while (rung > maxConfiguredRingFt * 1.4 && visible.length < 9) {
      const rounded = Math.round(rung);
      if (!visible.some((r) => Math.abs(r.radiusFt - rounded) < rounded * 0.18)) {
        visible.push({ radiusFt: rounded, color: ladderColor, fillOpacity: 0 });
      }
      rung /= 2;
    }
  }

  const rings: HouseRingDraw[] = [...visible]
    .sort((a, b) => b.radiusFt - a.radiusFt)
    .map((spec, i, arr) => ({
      radiusFt: spec.radiusFt,
      r: spec.radiusFt * ftToPx,
      stroke: spec.color ?? defaultRingColor,
      strokeWidth: i === arr.length - 1 ? 1.5 : 1.0,
      fill: spec.fillOpacity !== undefined && spec.color ? spec.color : 'none',
      fillOpacity: spec.fillOpacity ?? 0,
    }));

  const ringLabels: HouseRingLabel[] = visible.map((spec) => ({
    radiusFt: spec.radiusFt,
    x: ringLabelPosition === 'top' ? cx : cx + spec.radiusFt * ftToPx + 4,
    y:
      ringLabelPosition === 'top'
        ? Math.max(labelFontPx, cy - spec.radiusFt * ftToPx + labelFontPx + 2)
        : cy + labelFontPx * 0.4,
    anchor: ringLabelPosition === 'top' ? 'middle' : 'start',
  }));

  return {
    scale,
    ballRadiusPx,
    haloRadiusPx: ballRadiusPx + HALO_EXTRA_PX * scale,
    labelFontPx,
    cx,
    cy,
    ftToPx,
    outerRingFt,
    maxConfiguredRingFt,
    rings,
    ringLabels,
    toPxXY,
  };
}

/** The "winner's best ball": the closest-to-pin ball of the same side; ties
 *  pick the first. */
export function isWinnersBest(b: HouseBallInput, all: readonly HouseBallInput[]): boolean {
  const sameSide = all.filter((x) => x.side === b.side);
  let minR = Infinity;
  let bestOrder = -1;
  for (const x of sameSide) {
    const r = Math.hypot(x.longitudinalMissFt, x.lateralMissFt);
    if (r < minR) {
      minR = r;
      bestOrder = x.order;
    }
  }
  return b.order === bestOrder;
}

/** The ring label text the view prints, e.g. "3 FT" (value in the user's
 *  unit, rounded). `perFt` converts feet to the display unit. */
export function houseRingLabelText(radiusFt: number, perFt: number, unitLabel: string): string {
  return `${(radiusFt * perFt).toFixed(0)} ${unitLabel.toUpperCase()}`;
}
